import { DateRangePreset } from './DateRangePicker'
import {
    DateTimePicker as MuiDateTimePicker,
    DateTimePickerProps as MuiDateTimePickerProps,
} from '@mui/x-date-pickers'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import { IoCalendarOutline } from 'react-icons/io5'
import { FC, useCallback, useState } from 'react'
import { applyColorToPickerSubcomponents } from '../../lib/styles/universal'

export interface DateTimePickerProps
    extends Omit<MuiDateTimePickerProps<Date>, 'label'> {
    label?: string
    smallLabel?: boolean
    selectColor?: string
    iconColor?: string
    fullWidth?: boolean
    variableSize?: boolean
    bold?: boolean
    variant?: 'line' | 'box'
    detachedLabel?: boolean
    presets?: DateRangePreset[]
    onPresetChange?: (value: { startDate: Date; endDate: Date }) => void
    onChange?: (date: Date | null) => void
}

export const DateTimePicker: FC<DateTimePickerProps> = ({
    label,
    smallLabel,
    selectColor,
    iconColor,
    fullWidth,
    variableSize,
    bold,
    variant,
    detachedLabel,
    disabled,
    presets,
    onPresetChange,
    onChange,
    ...muiProps
}) => {
    const [activePreset, setActivePreset] = useState<string | null>(null)

    const handlePresetSelect = useCallback(
        (preset: DateRangePreset) => {
            const referenceDate = new Date()
            const { startDate, endDate } = preset.getValue(referenceDate)

            setActivePreset(preset.label)
            onChange?.(startDate)
            onPresetChange?.({ startDate, endDate })
        },
        [onChange, onPresetChange]
    )
    const render = () => (
        <>
            {detachedLabel && (
                <Typography
                    variant={smallLabel ? 'h5' : 'h4'}
                    pb="0.625rem"
                    color={selectColor}
                    fontStyle="normal"
                    fontWeight={500}
                >
                    {label}
                </Typography>
            )}
            <MuiDateTimePicker
                {...muiProps}
                onChange={onChange}
                disabled={disabled}
                disableFuture
                slots={{
                    openPickerIcon: () => (
                        <IoCalendarOutline color={iconColor} />
                    ),
                }}
                slotProps={{
                    textField: {
                        variant: variant === 'line' ? 'standard' : 'outlined',
                        sx: {
                            ...applyColorToPickerSubcomponents(selectColor),
                            fontWeight: bold ? 500 : 'normal',
                        },
                        label: detachedLabel ? undefined : label,
                        InputLabelProps: {
                            sx: {
                                fontWeight: '600',
                                color: selectColor || 'inherit',
                            },
                        },
                    },
                }}
            />
            {presets && presets.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                    {presets.map((preset) => (
                        <Button
                            key={preset.label}
                            size="small"
                            variant={activePreset === preset.label ? 'contained' : 'outlined'}
                            onClick={() => handlePresetSelect(preset)}
                            sx={{ borderRadius: '999px', textTransform: 'none' }}
                            type="button"
                        >
                            {preset.label}
                        </Button>
                    ))}
                </Box>
            ) : null}
        </>
    )

    return fullWidth ? (
        <FormControl fullWidth disabled={disabled}>
            {render()}
        </FormControl>
    ) : (
        <Box
            sx={{
                width: variableSize ? 'fit-content' : '100%',
                maxWidth: variableSize ? undefined : 400,
                height: 'auto',
            }}
        >
            <FormControl fullWidth disabled={disabled}>
                {render()}
            </FormControl>
        </Box>
    )
}